import React, { useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { Download, FileText, FileSpreadsheet, Printer, File, ChevronDown } from 'lucide-react';

/**
 * ExportBar — McKinsey-standard export component for Bangladesh education reports
 * Formats: PDF (McKinsey-styled HTML), CSV, Excel (styled), Word (formatted), Print
 */
export default function ExportBar({ title, data, columns, onExport }) {
  const { lang } = useApp();
  const [isOpen, setIsOpen] = useState(false);

  const timestamp = new Date().toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const safeTitle = (title || 'Report').replace(/[^a-zA-Z0-9\s]/g, '');

  const getTableRows = () => {
    if (!data || !columns) return { header: '', rows: '' };
    const header = columns.map(c => c.label).join(',');
    const rows = data.map((row, idx) => columns.map(c => {
      const val = typeof c.accessor === 'function' ? c.accessor(row, idx) : row[c.accessor];
      return val ?? '';
    }));
    return { header: columns.map(c => c.label), rows };
  };

  const mcKinseyStyles = `
    @page { size: A4 landscape; margin: 20mm; }
    body { font-family: 'Segoe UI', 'Hind Siliguri', Calibri, Arial, sans-serif; color: #1a1a1a; padding: 0; margin: 0; }
    .report-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 4px solid #006A4E; padding-bottom: 16px; margin-bottom: 24px; }
    .report-header h1 { font-size: 22px; color: #006A4E; margin: 0; font-weight: 700; letter-spacing: -0.5px; }
    .report-header .subtitle { font-size: 12px; color: #666; margin-top: 4px; }
    .report-header .meta { text-align: right; font-size: 10px; color: #888; }
    .report-header .meta .date { font-weight: 600; color: #333; font-size: 11px; }
    .red-bar { height: 3px; background: #F42A41; margin: 0 0 20px 0; }
    .kpi-row { display: flex; gap: 16px; margin-bottom: 24px; }
    .kpi-box { flex: 1; background: #f8f9fa; border: 1px solid #e9ecef; border-top: 3px solid #006A4E; padding: 12px 16px; }
    .kpi-box .value { font-size: 28px; font-weight: 700; color: #006A4E; }
    .kpi-box .label { font-size: 10px; color: #666; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 2px; }
    table { width: 100%; border-collapse: collapse; margin: 16px 0; font-size: 11px; }
    thead th { background: #006A4E; color: white; padding: 10px 12px; text-align: left; font-weight: 600; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
    tbody td { padding: 8px 12px; border-bottom: 1px solid #e9ecef; }
    tbody tr:nth-child(even) { background: #f8f9fa; }
    tbody tr:hover { background: #e8f5e9; }
    .footer { margin-top: 32px; padding-top: 12px; border-top: 2px solid #006A4E; display: flex; justify-content: space-between; font-size: 9px; color: #888; }
    .footer .left { display: flex; align-items: center; gap: 8px; }
    .footer .confidential { color: #F42A41; font-weight: 600; text-transform: uppercase; }
    .section-title { font-size: 14px; font-weight: 700; color: #006A4E; margin: 24px 0 8px 0; padding-bottom: 4px; border-bottom: 1px solid #e9ecef; }
    .bangla { font-family: 'Hind Siliguri', sans-serif; }
  `;

  const buildReport = (format) => {
    const { header, rows } = getTableRows();
    if (!header) return '';

    const totalRows = rows.length;
    let html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${safeTitle} — Shiksha BD</title><style>${mcKinseyStyles}</style></head><body>`;

    // McKinsey-style header
    html += `<div class="report-header">
      <div><h1>Shiksha BD — ${safeTitle}</h1><div class="subtitle bangla">শিক্ষা বিডি — ${safeTitle}</div>
      <div class="subtitle" style="margin-top:8px">Bangladesh Ministry of Education | BANBEIS 2024</div></div>
      <div class="meta"><div class="date">${timestamp}</div><div>37,489 Institutions | 667,099 Teachers</div><div>Report ID: SBD-${Date.now().toString(36).toUpperCase()}</div></div>
    </div>`;
    html += '<div class="red-bar"></div>';

    // KPI summary row
    if (totalRows > 0) {
      html += '<div class="kpi-row">';
      html += `<div class="kpi-box"><div class="value">${totalRows}</div><div class="label">Total Records</div></div>`;
      // Try to compute numeric summaries from first numeric column
      const numCols = header.filter((h, i) => rows.some(r => !isNaN(r[i]) && r[i] !== ''));
      if (numCols.length > 0) {
        const colIdx = header.indexOf(numCols[numCols.length - 1]);
        const vals = rows.map(r => parseFloat(r[colIdx])).filter(v => !isNaN(v));
        if (vals.length > 0) {
          html += `<div class="kpi-box"><div class="value">${Math.round(vals.reduce((a, b) => a + b, 0) / vals.length)}</div><div class="label">Average ${header[colIdx]}</div></div>`;
          html += `<div class="kpi-box"><div class="value">${Math.max(...vals)}</div><div class="label">Highest ${header[colIdx]}</div></div>`;
          html += `<div class="kpi-box"><div class="value">${Math.min(...vals)}</div><div class="label">Lowest ${header[colIdx]}</div></div>`;
        }
      }
      html += '</div>';
    }

    // Data table
    html += `<div class="section-title">Detailed Data</div>`;
    html += '<table><thead><tr>' + header.map(h => `<th>${h}</th>`).join('') + '</tr></thead><tbody>';
    rows.forEach(row => {
      html += '<tr>' + row.map(val => `<td>${val}</td>`).join('') + '</tr>';
    });
    html += '</tbody></table>';

    // Footer
    html += `<div class="footer">
      <div class="left"><span class="confidential">Confidential</span><span>Government of Bangladesh — Ministry of Education</span></div>
      <div>Generated by Shiksha BD Platform | ${timestamp} | Page 1 of 1</div>
    </div>`;
    html += '</body></html>';
    return html;
  };

  const exportCSV = () => {
    const { header, rows } = getTableRows();
    if (!header) return;
    const csv = [header.join(','), ...rows.map(r => r.map(v => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
    downloadFile('\uFEFF' + csv, `${safeTitle}.csv`, 'text/csv');
    setIsOpen(false);
  };

  const exportExcel = () => {
    downloadFile(buildReport('excel'), `${safeTitle}.xls`, 'application/vnd.ms-excel');
    setIsOpen(false);
  };

  const exportWord = () => {
    downloadFile(buildReport('word'), `${safeTitle}.doc`, 'application/msword');
    setIsOpen(false);
  };

  const exportPDF = () => {
    downloadFile(buildReport('pdf'), `${safeTitle}.html`, 'text/html');
    setIsOpen(false);
  };

  const handlePrint = () => {
    const html = buildReport('print');
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const printWin = window.open(url, '_blank');
    if (!printWin) { URL.revokeObjectURL(url); return; }
    printWin.addEventListener('load', () => {
      printWin.focus();
      setTimeout(() => { printWin.print(); URL.revokeObjectURL(url); }, 400);
    });
    setIsOpen(false);
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType + ';charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const formats = [
    { id: 'pdf', label: 'PDF Report', icon: FileText, color: '#C00000', action: exportPDF },
    { id: 'csv', label: 'CSV Data', icon: FileSpreadsheet, color: '#006A4E', action: exportCSV },
    { id: 'excel', label: 'Excel Workbook', icon: FileSpreadsheet, color: '#1D6F42', action: exportExcel },
    { id: 'word', label: 'Word Document', icon: File, color: '#2B579A', action: exportWord },
    { id: 'print', label: lang === 'bn' ? 'প্রিন্ট করুন' : 'Print Report', icon: Printer, color: '#333', action: handlePrint },
  ];

  return (
    <div data-testid="export-bar" style={{ position: 'relative', display: 'inline-flex' }}>
      <button onClick={() => setIsOpen(!isOpen)} className="btn btn-sm"
        style={{ background: 'var(--color-accent)', color: 'white', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', fontSize: 'var(--text-footnote)' }}>
        <Download size={14} />
        {lang === 'bn' ? 'রপ্তানি / ডাউনলোড' : 'Export / Download'}
        <ChevronDown size={12} style={{ opacity: 0.7 }} />
      </button>
      {isOpen && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setIsOpen(false)} />
          <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 6, background: 'var(--color-surface-1)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-xl)', border: '1px solid var(--color-separator)', zIndex: 100, minWidth: 220, overflow: 'hidden' }}>
            <div style={{ padding: '8px 14px', fontSize: 'var(--text-caption)', fontWeight: 600, color: 'var(--color-label-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', background: 'var(--color-background-raised)', borderBottom: '1px solid var(--color-separator)' }}>
              {lang === 'bn' ? 'ফরম্যাট নির্বাচন (McKinsey স্ট্যান্ডার্ড)' : 'Select Format (McKinsey Standard)'}
            </div>
            {formats.map(f => {
              const Icon = f.icon;
              return (
                <button key={f.id} data-testid={`export-${f.id}`} onClick={f.action}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 'var(--text-subhead)', color: 'var(--color-label-primary)', borderBottom: '1px solid var(--color-separator)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--color-background-raised)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <Icon size={16} style={{ color: f.color }} />
                  <span>{f.label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
